import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AdminRoles, AuthorizedRoles, DoctorRoles, PatientDataMode, PatientRoles, Phase, Role } from '../config/DiactiveTypes';
import { signOutUser } from '../services/AuthService';

import DiactiveLogo from '../assets/images/diactive-logo.png';

const drawerWidth = 300;

const navItems = [
  { name: 'Inicio', path: '/', roles: AuthorizedRoles },
  {
    name: 'Usuarios',
    roles: AdminRoles,
    children: [
      { name: 'Pacientes', path: '/', roles: AdminRoles },
      { name: 'Médicos', path: '/doctors/', roles: AdminRoles },
      { name: 'Administradores', path: '/admins/', roles: [Role.Admin] },
      { name: 'Testers de app', path: '/app-testers/', roles: AdminRoles },
    ],
  },
  {
    name: 'Contenido',
    roles: AdminRoles,
    children: [
      { name: 'Hospitales', path: '/hospitals/', roles: AdminRoles },
      { name: 'Tutoriales', path: '/admin/tutorials/', roles: AdminRoles },
    ],
  },
  {
    name: 'FAQs',
    roles: AdminRoles,
    children: [
      { name: 'Pacientes', path: '/admin/faqs/patients/', roles: AdminRoles },
      { name: 'Médicos', path: '/admin/faqs/doctors/', roles: AdminRoles },
    ],
  },
  {
    name: 'Incidencias',
    roles: AdminRoles,
    children: [
      { name: 'Manuales', path: '/incidents/', roles: AdminRoles },
      { name: 'Correo', path: '/pending-mail/', roles: AdminRoles },
    ],
  },
  { name: 'Tutoriales', path: '/tutorials', roles: [...DoctorRoles, ...PatientRoles] },
  { name: 'FAQs', path: '/faqs/', roles: [...DoctorRoles, ...PatientRoles] },
  { name: 'Añadir Paciente', path: `/patient/${PatientDataMode.Eval}/${Phase.PRE}`, roles: DoctorRoles },
  { name: 'Cerrar sesión', action: signOutUser, roles: AuthorizedRoles },
];

const title = 'Diactive-1';

const filterItemsForRole = (items, role) =>
  items
    .filter((item) => item.roles.includes(role))
    .map((item) => {
      if (!item.children) return item;
      const visibleChildren = item.children.filter((child) => child.roles.includes(role));
      return { ...item, children: visibleChildren };
    })
    .filter((item) => !item.children || item.children.length > 0);

function DrawerAppBar(props) {
  const navigate = useNavigate();
  const { window, isAuth, role } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuState, setMenuState] = useState({ name: null, anchorEl: null });
  const [openGroups, setOpenGroups] = useState({});
  const closeMenuTimeoutRef = useRef(null);

  if (!isAuth) return null;

  const itemsForRole = filterItemsForRole(navItems, role);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const cancelScheduledClose = () => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  };

  const openMenu = (name, anchorEl) => {
    cancelScheduledClose();
    setMenuState({ name, anchorEl });
  };
  const closeMenu = () => {
    cancelScheduledClose();
    setMenuState({ name: null, anchorEl: null });
  };
  const scheduleCloseMenu = () => {
    cancelScheduledClose();
    closeMenuTimeoutRef.current = setTimeout(() => {
      setMenuState({ name: null, anchorEl: null });
      closeMenuTimeoutRef.current = null;
    }, 150);
  };

  const toggleGroup = (name) =>
    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));

  const renderDrawerItem = (item) => {
    if (item.children) {
      const isOpen = !!openGroups[item.name];
      return (
        <Box key={item.name} onClick={(e) => e.stopPropagation()}>
          <ListItem disablePadding>
            <ListItemButton
              sx={{ textAlign: 'center', color: '#fff' }}
              onClick={() => toggleGroup(item.name)}
            >
              <ListItemText primary={item.name} sx={{ textAlign: 'center' }} />
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => (
                <ListItem key={child.name} disablePadding>
                  <ListItemButton
                    sx={{ textAlign: 'center', color: '#fff', pl: 4 }}
                    component={Link}
                    to={child.path}
                    onClick={handleDrawerToggle}
                  >
                    <ListItemText
                      primary={child.name}
                      slotProps={{ primary: { sx: { fontWeight: 500 } } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem key={item.name} disablePadding>
        <ListItemButton
          sx={{ textAlign: 'center', color: '#fff' }}
          component={item.path ? Link : 'button'}
          to={item.path}
          onClick={item.action}
        >
          <ListItemText primary={item.name} />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderAppBarItem = (item) => {
    if (item.children) {
      const isMenuOpen = menuState.name === item.name;
      return (
        <Box key={item.name} sx={{ display: 'inline-block' }}>
          <Button
            sx={{ color: '#fff' }}
            endIcon={<KeyboardArrowDown />}
            onClick={(e) => openMenu(item.name, e.currentTarget)}
            onMouseEnter={(e) => openMenu(item.name, e.currentTarget)}
            onMouseLeave={scheduleCloseMenu}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            {item.name}
          </Button>
          <Menu
            anchorEl={menuState.anchorEl}
            open={isMenuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{ pointerEvents: 'none' }}
            slotProps={{
              paper: {
                onMouseEnter: cancelScheduledClose,
                onMouseLeave: scheduleCloseMenu,
                sx: {
                  pointerEvents: 'auto',
                  bgcolor: 'primary.main',
                  color: '#fff',
                  mt: 0.5,
                  minWidth: 180,
                },
              },
              list: { sx: { py: 0.5 } },
            }}
            autoFocus={false}
            disableAutoFocusItem
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
          >
            {item.children.map((child) => (
              <MenuItem
                key={child.name}
                component={Link}
                to={child.path}
                onClick={closeMenu}
                sx={{
                  color: '#fff',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                }}
              >
                {child.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      );
    }

    return (
      <Button
        key={item.name}
        sx={{ color: '#fff' }}
        component={item.path ? Link : 'button'}
        to={item.path}
        onClick={item.action}
      >
        {item.name}
      </Button>
    );
  };

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          bgcolor: 'white',
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: 3,
          mt: 1.5,
          mb: 1.5
        }}
        component={Link}
        to={'/'}
      >
        <img src={DiactiveLogo} alt="Diactive Logo" style={{ width: '80%', height: '80%' }} />
      </Box>
      <List sx={{ width: '100%' }}>
        {itemsForRole.map(renderDrawerItem)}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#fff' }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              width: 52,
              height: 52,
              bgcolor: 'white',
              borderRadius: '50%',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: 3,
              ml: 10,
              mr: 2
            }}
            component={Link}
            to={'/'}
          >
            <img src={DiactiveLogo} alt="Diactive Logo" style={{ width: '80%', height: '80%' }} />
          </Box>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' }, color: '#fff', cursor: 'pointer' }}
            onClick={() => navigate("/")}
          >
            {title}
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {itemsForRole.map(renderAppBarItem)}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, bgcolor: 'primary.main' },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <Box component="main" sx={{ p: 0 }}>
        <Toolbar />
      </Box>
    </Box>
  );
}

export default DrawerAppBar;
